import { useState } from 'react'
import { AxiosResponse } from 'axios'
import { axiosInstance } from '../helpers'
import {
    Damage,
    DamageStatus,
    DamageTransaction,
    Lease,
    DamageStatusCounts,
    DamageStatusList,
    LeaseStatus,
} from '../models'
import { toast } from 'react-toastify'

export interface DamageRequestFilters {
    statuses?: string
    excludeDenied?: boolean
    unitIds?: string
    issues?: boolean
    inspection?: number
    sort_by_created_date?: boolean
    created_before?: string
    created_after?: string
    search?: string
    hasAttachment?: boolean
    hasMatchingIssue?: boolean
    cost?: number
    cost_operator?: string
}

export const usePaginatedDamageList = (
    page_size: number,
    onSuccess?: (response: AxiosResponse<any>, damageList: Damage[]) => void,
    onError?: (e: any) => void,
    cleanUp?: () => void,
) => {
    const [loadingDamages, setLoadingDamages] = useState(false)

    const [damageList, setDamageList] = useState<Damage[]>()
    const [page, setPage] = useState(1)
    const [totalDamageCount, setTotalDamageCount] = useState(0)
    const [statusCounts, setStatusCounts] = useState<DamageStatusCounts | null>(
        null,
    )
    /** All damage PKs for current list filters (from `damage-ids`, refreshed with each `getDamageList`) */
    const [matchingDamageIds, setMatchingDamageIds] = useState<number[]>([])

    const handleCleanUp = () => {
        cleanUp && cleanUp()
        setTimeout(() => setLoadingDamages(false), 500)
    }

    const listFilterParams = (filters?: DamageRequestFilters) => ({
        transactions_created: false,
        statuses: filters?.statuses,
        unitIds: filters?.unitIds,
        issues: filters?.issues,
        inspection: filters?.inspection,
        sort_by_created_date: filters?.sort_by_created_date,
        created_before: filters?.created_before,
        created_after: filters?.created_after,
        search: filters?.search,
        has_attachment: filters?.hasAttachment,
        has_matching_issue: filters?.hasMatchingIssue,
        cost: filters?.cost,
        cost_operator: filters?.cost_operator,
    })

    const getDamageList = (page: number, filters?: DamageRequestFilters) => {
        setLoadingDamages(true)
        const base = listFilterParams(filters)
        const limit = page_size
        const offset = (page - 1) * page_size

        Promise.allSettled([
            axiosInstance.get('lease/damage/', {
                params: {
                    ...base,
                    limit,
                    offset,
                },
            }),
            axiosInstance.get('lease/damage/damage-ids/', {
                params: base,
            }),
        ])
            .then((results) => {
                const listResult = results[0]
                const idsResult = results[1]

                if (listResult.status === 'fulfilled') {
                    const res = listResult.value
                    onSuccess && onSuccess(res, res.data.results)
                    setDamageList(res.data.results)
                    setTotalDamageCount(res.data.count)

                    if (idsResult.status === 'fulfilled') {
                        const raw = idsResult.value.data?.damage_ids
                        setMatchingDamageIds(
                            Array.isArray(raw)
                                ? raw.map((id: unknown) => Number(id))
                                : [],
                        )
                    } else {
                        setMatchingDamageIds([])
                    }
                } else {
                    onError?.(listResult.reason)
                    setMatchingDamageIds([])
                }
            })
            .finally(handleCleanUp)
    }

    const getDamageStatusCounts = async (filters?: DamageRequestFilters) => {
        try {
            const res = await axiosInstance.get(
                'lease/damage/get_status_counts/',
                {
                    params: {
                        statuses: filters?.statuses,
                        unitIds: filters?.unitIds,
                        issues: filters?.issues,
                        inspection: filters?.inspection,
                        sort_by_created_date: filters?.sort_by_created_date,
                        created_before: filters?.created_before,
                        created_after: filters?.created_after,
                        search: filters?.search,
                        has_attachment: filters?.hasAttachment,
                        has_matching_issue: filters?.hasMatchingIssue,
                        cost: filters?.cost,
                        cost_operator: filters?.cost_operator,
                    },
                },
            )
            // Convert string keys to DamageStatus enum values
            const counts: DamageStatusCounts = {
                [DamageStatus.PENDING]: res.data[DamageStatus.PENDING] ?? 0,
                [DamageStatus.PRE_APPROVED]:
                    res.data[DamageStatus.PRE_APPROVED] ?? 0,
                [DamageStatus.APPROVED]: res.data[DamageStatus.APPROVED] ?? 0,
                [DamageStatus.SYNC_PENDING]:
                    res.data[DamageStatus.SYNC_PENDING] ?? 0,
                [DamageStatus.ENTRATA_SYNCED]:
                    res.data[DamageStatus.ENTRATA_SYNCED] ?? 0,
                [DamageStatus.DENIED]: res.data[DamageStatus.DENIED] ?? 0,
                [DamageStatus.SYNC_FAILED]:
                    res.data[DamageStatus.SYNC_FAILED] ?? 0,
            }
            setStatusCounts(counts)
        } catch (e) {
            Promise.reject(e)
        } finally {
            handleCleanUp()
        }
    }

    const updateDamageStatus = (
        damages: Damage[],
        status: DamageStatus,
        options?: {
            /** When true (e.g. select-all via id list without full rows), skip client AR check */
            skipApprovedTransactionCheck?: boolean
            /** When true, refetch counts instead of optimistic local adjustment */
            skipOptimisticCounts?: boolean
        },
    ): Promise<void> | undefined => {
        let shouldAlert = false

        if (
            status === DamageStatus.APPROVED &&
            !options?.skipApprovedTransactionCheck
        ) {
            damages.forEach((dmg) => {
                shouldAlert = dmg.damage_transactions.length === 0
            })
        }

        if (shouldAlert) {
            if (
                !confirm(
                    'One or more of your selected damages do not have any AR Transactions.  Damages without transactions will not be able to sync.',
                )
            ) {
                return undefined
            }
        }

        setLoadingDamages(true)
        return axiosInstance
            .post('lease/damage/update_status/', {
                damages: damages.map((d) => d.id),
                status: status,
            })
            .then((res) => {
                updateDamageState(res.data)
                onSuccess && onSuccess(res, damageList ?? [])

                if (!options?.skipOptimisticCounts) {
                    // Update status counts locally (no refetch)
                    const newCounts: DamageStatusCounts = statusCounts
                        ? { ...statusCounts }
                        : (Object.fromEntries(
                              DamageStatusList.map((s) => [s, 0]),
                          ) as DamageStatusCounts)
                    damages.forEach((damage) => {
                        newCounts[damage.status] = Math.max(
                            0,
                            (newCounts[damage.status] || 0) - 1,
                        )
                        newCounts[status] = (newCounts[status] || 0) + 1
                    })
                    setStatusCounts(newCounts)
                }
            })
            .catch((e) => {
                onError?.(e)
                return Promise.reject(e)
            })
            .finally(handleCleanUp)
    }

    const updateDamage = async (
        damage: Damage,
        request: UpdateDamageRequest,
    ) => {
        try {
            const res = await axiosInstance.patch(
                `lease/damage/${damage.id}/`,
                request,
            )
            const updatedDamage: Damage = res.data
            updateDamageState([updatedDamage])
            return updatedDamage
        } catch (e) {
            return Promise.reject(e)
        }
    }

    const removeDamageTransaction = (
        transaction: DamageTransaction,
        damage: Damage,
    ) => {
        setLoadingDamages(true)
        axiosInstance
            .post(`lease/damage/${damage.id}/remove_transaction/`, {
                transaction: transaction.id,
            })
            .then((res) => {
                updateDamageState([
                    {
                        ...damage,
                        damage_transactions: damage.damage_transactions.filter(
                            (dt) => dt.id !== transaction.id,
                        ),
                    },
                ])
                onSuccess && onSuccess(res, damageList ?? [])
            })
            .catch(onError)
            .finally(handleCleanUp)
    }

    const addDamageTransaction = (lease: Lease, damage: Damage) => {
        setLoadingDamages(true)
        axiosInstance
            .post(`lease/damage/${damage.id}/add_transaction/`, {
                tenant: lease.id,
            })
            .then((res) => {
                updateDamageState([res.data])
                onSuccess && onSuccess(res, damageList ?? [])
            })
            .catch(onError)
            .finally(handleCleanUp)
    }

    const updateDamageState = (updatedDamages: Damage[]) => {
        if (damageList === undefined) {
            return
        }

        const newDamageList: Damage[] = []

        let resIdx = 0
        for (let dmgIdx = 0; dmgIdx < damageList.length; dmgIdx++) {
            const currentDamage = damageList[dmgIdx]

            if (resIdx >= updatedDamages.length) {
                // If we have exhausted all of the udpated damages
                // push the remaining current damages
                newDamageList.push(currentDamage)
                continue
            }

            const updatedDamage = updatedDamages[resIdx]

            if (currentDamage.id === updatedDamage.id) {
                // The current damage was updated,
                // use the updated version and increment the response index
                newDamageList.push(updatedDamage)
                resIdx += 1
            } else {
                // This damage was not updated,
                // keep it and move on
                newDamageList.push(currentDamage)
            }
        }

        setDamageList(newDamageList)
    }

    const removeAllPastTransactions = () => {
        setLoadingDamages(true)
        axiosInstance
            .post('lease/damage/delete_past_transactions/')
            .then(() => {
                const updatedDamageList = damageList?.map((damage) => {
                    if (damage.status !== DamageStatus.PENDING) {
                        return damage
                    }
                    return {
                        ...damage,
                        damage_transactions: damage.damage_transactions.filter(
                            (dt) => dt.lease.status !== LeaseStatus.PAST,
                        ),
                    }
                })
                setDamageList(updatedDamageList)
                toast.success('All past transactions have been deleted')
            })
            .catch(onError)
            .finally(handleCleanUp)
    }

    return {
        damageList,
        getDamageList,
        loadingDamages,
        currentPage: page,
        setPage,
        totalDamageCount,
        updateDamageStatus,
        removeDamageTransaction,
        addDamageTransaction,
        updateDamageState,
        updateDamage,
        statusCounts,
        getDamageStatusCounts,
        removeAllPastTransactions,
        matchingDamageIds,
    }
}

export interface UpdateDamageRequest {
    damage_config?: number
    cost?: string
    amount?: number
    attachment_to_sync?: number
}
